import { useState, useEffect } from 'react';

export type ThemeMode = 'light' | 'dark';

interface OfficeTheme {
  bodyBackgroundColor: string;
  bodyForegroundColor: string;
  controlBackgroundColor: string;
  controlForegroundColor: string;
}

export function useOfficeTheme(): ThemeMode {
  const [theme, setTheme] = useState<ThemeMode>('light');

  useEffect(() => {
    const detectTheme = () => {
      // Try to detect from Office.js theme
      if (typeof Office !== 'undefined' && Office.context?.officeTheme) {
        const officeTheme = Office.context.officeTheme as OfficeTheme;
        // Check if background is dark (low luminance)
        const bgColor = officeTheme.bodyBackgroundColor;
        if (bgColor) {
          const isDark = isColorDark(bgColor);
          setTheme(isDark ? 'dark' : 'light');
          return;
        }
      }

      // Fallback: check system preference
      if (window.matchMedia) {
        const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
        setTheme(darkModeQuery.matches ? 'dark' : 'light');

        // Listen for changes
        const handler = (e: MediaQueryListEvent) => {
          setTheme(e.matches ? 'dark' : 'light');
        };
        darkModeQuery.addEventListener('change', handler);
        return () => darkModeQuery.removeEventListener('change', handler);
      }
    };

    detectTheme();

    // Office theme change handler
    if (typeof Office !== 'undefined' && Office.context?.document) {
      Office.context.document.addHandlerAsync(
        Office.EventType.DocumentSelectionChanged,
        detectTheme,
      );
    }
  }, []);

  return theme;
}

// Helper to determine if a color is dark
function isColorDark(color: string): boolean {
  // Handle hex colors
  let r = 0,
    g = 0,
    b = 0;

  if (color.startsWith('#')) {
    const hex = color.slice(1);
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    }
  } else if (color.startsWith('rgb')) {
    const match = color.match(/\d+/g);
    if (match && match.length >= 3) {
      r = parseInt(match[0]);
      g = parseInt(match[1]);
      b = parseInt(match[2]);
    }
  }

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}
